import axios from 'axios';
import { apiBaseUrl } from './apiBase';

const api = axios.create({
  baseURL: apiBaseUrl(),
  timeout: 60000,
});

function headerGet(headers, name) {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') return headers.get(name);
  return headers[name] ?? headers[name.toLowerCase()];
}

function headerSet(headers, name, value) {
  if (!headers) return;
  if (typeof headers.set === 'function') headers.set(name, value);
  else headers[name] = value;
}

function headerDelete(headers, name) {
  if (!headers) return;
  if (typeof headers.delete === 'function') {
    headers.delete(name);
    headers.delete(name.toLowerCase());
  } else {
    delete headers[name];
    delete headers[name.toLowerCase()];
    delete headers['Content-Type'];
  }
}

function emitAppStatus(type) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('nv-status', { detail: { type } }));
}

api.interceptors.request.use((config) => {
  // FormData must keep the browser-generated multipart boundary
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    headerDelete(config.headers, 'Content-Type');
  }

  const userToken = localStorage.getItem('nv_token');
  const adminToken = localStorage.getItem('nv_admin_token');
  const url = String(config.url || '');

  const isPublicAuth =
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/admin/login');

  if (isPublicAuth) {
    return config;
  }

  const wantsAdmin =
    headerGet(config.headers, 'X-Admin') === '1' ||
    /\/admin(\/|$)/.test(url) ||
    url.includes('/auth/admin/');

  if (wantsAdmin && adminToken) {
    headerSet(config.headers, 'Authorization', `Bearer ${adminToken}`);
  } else if (userToken) {
    headerSet(config.headers, 'Authorization', `Bearer ${userToken}`);
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('nv-slow-start'));
  }

  return config;
});

api.interceptors.response.use(
  (res) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('nv-slow-end'));
    }
    return res;
  },
  (err) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('nv-slow-end'));
    }

    const status = err.response?.status;
    const url = String(err.config?.url || '');
    const isAuthProbe =
      url.includes('/auth/me') ||
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/verify') ||
      url.includes('/auth/resend') ||
      url.includes('/auth/admin/login');

    if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
      err.message =
        'Network error — API unreachable or blocked (check server is running on :5000 and CORS/X-Admin).';
      if (typeof window !== 'undefined' && !navigator.onLine) {
        emitAppStatus('offline');
      }
    } else if (status === 401 && !isAuthProbe) {
      localStorage.removeItem('nv_token');
      emitAppStatus('timeout');
    } else if (status === 403) {
      emitAppStatus('denied');
    }

    return Promise.reject(err);
  }
);

export default api;
